// src/pages/AboutPage.js
import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import Reveal from '../components/layout/Reveal';
import { Breadcrumb, usePageTitle } from '../components/layout/PageBits';
import { IMG } from '../data/media';

// Story first, then the founder, why people choose us, and the timeline (Zerodha-style "about").
const C = {
  en: {
    kick: 'About us', title: 'A Haldia family business, built on trust.',
    story: ['Kalyani Consultancy helps families and businesses in and around Haldia protect what matters and save for what comes next: life, health, vehicle and home insurance, mutual funds and goal planning.', 'We are not here to push a product. We sit with you, explain your choices in plain words (English or Bengali), and stay with you when it is time to renew or claim.'],
    quote: 'Our mission is simple: to build lasting relationships built on trust. We are not here to sell a product; we are here to find the solution that is right for you.',
    whyTitle: 'Why families choose us',
    why: [['🏅', '20+ years of experience', 'Two decades of industry experience behind every recommendation.'], ['🤝', 'Clients who stay', 'Families who trust us with their protection. (Client numbers to confirm.)'], ['⚖', 'Honest guidance', 'The right policy for your family, explained in plain words.'], ['🏠', 'One place for everything', 'Insurance, mutual funds and tax planning under one roof.']],
    tlTitle: 'Our journey',
    timeline: [['2005 – 2020', '20+ years in insurance', 'Tusar-da builds his experience across the insurance industry.'], ['2020', 'Kalyani Consultancy is born', 'A local office in Haldia, with one promise: honest, transparent advice in plain words.'], ['2020 – today', 'Growing with Haldia', 'More families, more insurers and fund houses, and a new online home. (Milestones to confirm.)'], ['Next', 'Your partner for life', 'Renewals, reviews and claims: we stay with you.']],
    cta: 'Book a free consultation',
  },
  bn: {
    kick: 'আমাদের কথা', title: 'ভরসার উপর গড়া হলদিয়ার একটি পারিবারিক প্রতিষ্ঠান।',
    story: ['কল্যাণী কনসালটেন্সি হলদিয়া ও আশেপাশের পরিবার ও ব্যবসাকে সুরক্ষা ও সঞ্চয়ে সাহায্য করে: জীবন, স্বাস্থ্য, গাড়ি ও গৃহ বিমা, মিউচুয়াল ফান্ড এবং লক্ষ্যভিত্তিক পরিকল্পনা।', 'আমরা কোনো প্রোডাক্ট চাপিয়ে দিই না। আপনার সঙ্গে বসে সহজ ভাষায় (বাংলা বা ইংরেজিতে) বিকল্পগুলো বুঝিয়ে বলি, আর রিনিউয়াল বা ক্লেমের সময় পাশে থাকি।'],
    quote: 'আমাদের লক্ষ্য সহজ: ভরসার উপর দীর্ঘস্থায়ী সম্পর্ক গড়ে তোলা। আমরা প্রোডাক্ট বিক্রি করতে আসিনি; আপনার জন্য যা ঠিক, সেই সমাধান খুঁজে দিতে এসেছি।',
    whyTitle: 'পরিবারগুলো কেন আমাদের বেছে নেয়',
    why: [['🏅', '২০+ বছরের অভিজ্ঞতা', 'প্রতিটি পরামর্শের পিছনে দুই দশকের অভিজ্ঞতা।'], ['🤝', 'যে গ্রাহকরা থেকে যান', 'যে পরিবারগুলো তাদের সুরক্ষার ভার আমাদের দেয়। (গ্রাহক সংখ্যা নিশ্চিত করতে হবে।)'], ['⚖', 'সৎ নির্দেশনা', 'আপনার পরিবারের জন্য সঠিক পলিসি, সহজ ভাষায়।'], ['🏠', 'সব এক জায়গায়', 'বিমা, মিউচুয়াল ফান্ড ও কর পরিকল্পনা এক ছাদের নিচে।']],
    tlTitle: 'আমাদের যাত্রা',
    timeline: [['২০০৫ – ২০২০', 'বিমা শিল্পে ২০+ বছর', 'বিমা শিল্পের নানা ক্ষেত্রে তুষারদার অভিজ্ঞতা গড়ে ওঠে।'], ['২০২০', 'কল্যাণী কনসালটেন্সির জন্ম', 'হলদিয়ায় একটি স্থানীয় অফিস, একটাই প্রতিশ্রুতি: সহজ ভাষায় সৎ ও স্বচ্ছ পরামর্শ।'], ['২০২০ – আজ', 'হলদিয়ার সঙ্গে বেড়ে ওঠা', 'আরও পরিবার, আরও বিমা সংস্থা ও ফান্ড হাউস, আর নতুন অনলাইন ঠিকানা। (মাইলফলক নিশ্চিত করতে হবে।)'], ['এরপর', 'আজীবনের সঙ্গী', 'রিনিউয়াল, রিভিউ আর ক্লেম: আমরা পাশে থাকি।']],
    cta: 'বিনামূল্যে পরামর্শ বুক করুন',
  },
};

function AboutPage() {
  const { lang, t } = useLang();
  const c = C[lang];
  usePageTitle(c.kick);
  return (
    <>
      <section className="bg-white py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Breadcrumb current={c.kick} />
          <p className="kicker text-saffron">{c.kick}</p>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">{c.title}</h1>
          {c.story.map((p) => <p key={p} className="mt-5 text-lg text-slate-600 leading-relaxed">{p}</p>)}
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-[0.9fr_1.1fr] gap-12 items-center">
          <Reveal className="max-w-sm w-full mx-auto"><img src={IMG.founder} alt={t('phFounder')} loading="lazy" decoding="async" className="w-full aspect-[4/5] object-cover rounded-[2rem] shadow-xl" /></Reveal>
          <Reveal>
            <p className="kicker text-saffron">{t('fKick')}</p>
            <h2 className="h2">Tusar Kumar Bera</h2>
            <blockquote className="mt-5 text-xl sm:text-2xl font-semibold leading-snug border-l-4 border-saffron pl-5">{c.quote}</blockquote>
            <p className="mt-5 text-slate-600 leading-relaxed">{t('fText')}</p>
            <div className="mt-5 text-4xl font-sign">Tusar Kumar Bera</div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal><h2 className="h2 mb-8">{c.whyTitle}</h2></Reveal>
          <Reveal className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {c.why.map(([ic, h, p]) => (
              <div key={h} className="rounded-3xl bg-sky p-6"><div className="text-3xl" aria-hidden="true">{ic}</div><h3 className="mt-3 font-bold text-lg">{h}</h3><p className="mt-1 text-slate-600 text-sm leading-relaxed">{p}</p></div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="bg-night text-white py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Reveal><h2 className="h2 mb-10">{c.tlTitle}</h2></Reveal>
          <ol className="relative border-l-2 border-white/15 ml-3 space-y-10">
            {c.timeline.map(([when, h, p]) => (
              <Reveal as="li" key={when} className="pl-8 relative">
                <span className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-saffron ring-4 ring-night" aria-hidden="true" />
                <div className="text-amber-200 font-bold">{when}</div>
                <h3 className="mt-1 text-xl font-extrabold">{h}</h3>
                <p className="mt-1 text-white/70">{p}</p>
              </Reveal>
            ))}
          </ol>
          <Link to="/contact" className="mt-12 inline-flex bg-white text-ink font-bold px-6 py-3.5 rounded-full">{c.cta} →</Link>
        </div>
      </section>
    </>
  );
}

export default AboutPage;
